import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import {
  ScheduleItemCreateDto,
  ScheduleItemDeleteDto,
  ScheduleItemDto,
  ScheduleItemUpdateDto,
} from "./calendar.types";
import calendarService from "./scheduleItem.service";
import {
  requestNative,
  supportsNativeFeature,
} from "../../services/nativeBridge";

export function isTimeframeLoaded(
  newStart: string,
  newEnd: string,
  loadedRanges: { startDate: string; endDate: string }[],
): boolean {
  const newStartDate = new Date(newStart).getTime();
  const newEndDate = new Date(newEnd).getTime();

  return loadedRanges.some(({ startDate, endDate }) => {
    const rangeStart = new Date(startDate).getTime();
    const rangeEnd = new Date(endDate).getTime();
    return newStartDate >= rangeStart && newEndDate <= rangeEnd;
  });
}

function getMissingTimeframes(
  requestedStart: string,
  requestedEnd: string,
  loadedRanges: { startDate: string; endDate: string }[],
): { startDate: string; endDate: string }[] {
  const requestStart = new Date(requestedStart).getTime();
  const requestEnd = new Date(requestedEnd).getTime();

  if (requestStart >= requestEnd) return [];

  // Sort loaded ranges by start date
  const sortedRanges = [...loadedRanges]
    .map(({ startDate, endDate }) => ({
      start: new Date(startDate).getTime(),
      end: new Date(endDate).getTime(),
    }))
    .sort((a, b) => a.start - b.start);

  let current = requestStart;
  const missing: { startDate: string; endDate: string }[] = [];

  for (const range of sortedRanges) {
    if (range.end < current) continue; // This range is entirely before the current start
    if (range.start > current) {
      // There's a gap before this range
      missing.push({
        startDate: new Date(current).toISOString(),
        endDate: new Date(Math.min(range.start, requestEnd)).toISOString(),
      });
    }
    current = Math.max(current, range.end);
    if (current >= requestEnd) break;
  }

  // If there's remaining gap after all loaded ranges
  if (current < requestEnd) {
    missing.push({
      startDate: new Date(current).toISOString(),
      endDate: new Date(requestEnd).toISOString(),
    });
  }

  return missing;
}

export const createScheduleItem = createAsyncThunk<
  ScheduleItemDto,
  ScheduleItemCreateDto,
  { state: RootState }
>(
  "calendar/createScheduleItem",
  async (scheduleItem: ScheduleItemCreateDto, thunkAPI) => {
    try {
      return await calendarService.createScheduleItem(scheduleItem);
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const createScheduleItemNote = createAsyncThunk<
  ScheduleItemDto,
  ScheduleItemCreateDto,
  { state: RootState }
>(
  "calendar/createScheduleItemNote",
  async (scheduleItem: ScheduleItemCreateDto, thunkAPI) => {
    try {
      return await calendarService.createScheduleItemNote(scheduleItem);
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const initializeCalendar = createAsyncThunk<
  ScheduleItemDto[],
  { timeMin?: string; timeMax?: string },
  { state: RootState }
>("calendar/initializeCalendar", async ({ timeMin, timeMax }, thunkAPI) => {
  try {
    return await calendarService.getCalendarItems(timeMin, timeMax);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getScheduleItems = createAsyncThunk<
  ScheduleItemDto[],
  void,
  { state: RootState }
>("calendar/getScheduleItems", async (_, thunkAPI) => {
  try {
    return await calendarService.getScheduleItems();
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getRoutines = createAsyncThunk<
  ScheduleItemDto[],
  void,
  { state: RootState }
>("calendar/getRoutines", async (_, thunkAPI) => {
  try {
    return await calendarService.getRoutines();
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const getScheduleItem = createAsyncThunk<
  ScheduleItemDto,
  string,
  { state: RootState }
>("calendar/getScheduleItem", async (id, thunkAPI) => {
  try {
    return await calendarService.getScheduleItem(id);
  } catch (error: any) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateScheduleItem = createAsyncThunk<
  ScheduleItemDto,
  ScheduleItemUpdateDto,
  { state: RootState }
>(
  "calendar/updateScheduleItem",
  async (scheduleItem: ScheduleItemUpdateDto, thunkAPI) => {
    try {
      return await calendarService.updateScheduleItem(scheduleItem);
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const updateScheduleItemNote = createAsyncThunk<
  ScheduleItemDto,
  ScheduleItemUpdateDto,
  { state: RootState }
>(
  "calendar/updateScheduleItemNote",
  async (scheduleItem: ScheduleItemUpdateDto, thunkAPI) => {
    try {
      return await calendarService.updateScheduleItemNote(scheduleItem);
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const deleteScheduleItem = createAsyncThunk<
  ScheduleItemDeleteDto,
  ScheduleItemDeleteDto,
  { state: RootState }
>(
  "calendar/deleteScheduleItem",
  async (scheduleItem: ScheduleItemDeleteDto, thunkAPI) => {
    try {
      return await calendarService.deleteScheduleItem(scheduleItem);
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const deleteScheduleItemNote = createAsyncThunk<
  ScheduleItemDeleteDto,
  ScheduleItemDeleteDto,
  { state: RootState }
>(
  "calendar/deleteScheduleItemNote",
  async (scheduleItem: ScheduleItemDeleteDto, thunkAPI) => {
    try {
      return await calendarService.deleteScheduleItemNote(scheduleItem);
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      console.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const getAPICalendarEntries = createAsyncThunk<
  { items: ScheduleItemDto[]; timeMin: string; timeMax: string },
  { timeMin: string; timeMax: string },
  { state: RootState }
>("calendar/getAPICalendarEntries", async ({ timeMin, timeMax }, thunkAPI) => {
  const state = thunkAPI.getState();

  // The phone's calendar is read locally by the app, uncached
  if (supportsNativeFeature("deviceCalendar")) {
    thunkAPI.dispatch(getDeviceCalendarEntries({ timeMin, timeMax }));
  }

  const loadedRanges =
    state.calendar.loadingCache.externalCalendarItemsLoadedTimeframes;

  const results: ScheduleItemDto[] = [];

  // Find only the missing timeframes
  const missingRanges = getMissingTimeframes(timeMin, timeMax, loadedRanges);

  if (missingRanges.length === 0) {
    return { items: [], timeMin, timeMax }; // Everything is already loaded
  }

  // Fetch calendar entries for each missing sub-range
  for (const range of missingRanges) {
    if (
      state.user.userAPIConnections.some(
        (a) => a.provider === "google" && a.type === "calendar",
      )
    ) {
      const googleResult = await calendarService.getGoogleCalendarEntries(
        range.startDate,
        range.endDate,
      );
      results.push(...googleResult);
    }

    if (
      state.user.userAPIConnections.some(
        (a) => a.provider === "microsoft" && a.type === "calendar",
      )
    ) {
      const microsoftResult = await calendarService.getMicrosoftCalendarEntries(
        range.startDate,
        range.endDate,
      );
      results.push(...microsoftResult);
    }
  }

  return { items: results, timeMin, timeMax };
});

// Events of the calendars on the phone, read by the native app (expo-calendar).
// They never reach the backend.
export const getDeviceCalendarEntries = createAsyncThunk<
  { items: ScheduleItemDto[]; timeMin: string; timeMax: string },
  { timeMin: string; timeMax: string }
>(
  "calendar/getDeviceCalendarEntries",
  async ({ timeMin, timeMax }, thunkAPI) => {
    try {
      const items = await requestNative<ScheduleItemDto[]>({
        type: "DEVICE_CALENDAR_EVENTS",
        timeMin,
        timeMax,
      });
      return { items, timeMin, timeMax };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error?.message || String(error));
    }
  },
);

// Reloads everything loaded from the phone so far, e.g. after the app comes
// back to the foreground or the calendar selection changed
export const refreshDeviceCalendarEntries = createAsyncThunk<
  void,
  void,
  { state: RootState }
>("calendar/refreshDeviceCalendarEntries", async (_, thunkAPI) => {
  const range = thunkAPI.getState().calendar.loadingCache.deviceCalendarRange;
  if (!range || !supportsNativeFeature("deviceCalendar")) return;
  await thunkAPI.dispatch(
    getDeviceCalendarEntries({
      timeMin: range.startDate,
      timeMax: range.endDate,
    }),
  );
});

export const getCalendarItems = createAsyncThunk<
  ScheduleItemDto[],
  { timeMin?: string; timeMax?: string },
  { state: RootState }
>("calendar/getCalendarItems", async ({ timeMin, timeMax }, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const loadedRanges =
      state.calendar.loadingCache.calendarItemsLoadedTimeframes;

    if (timeMin && timeMax) {
      const missingRanges = getMissingTimeframes(
        timeMin,
        timeMax,
        loadedRanges,
      );

      if (missingRanges.length === 0) {
        return []; // Fully cached
      }

      // Fetch only missing parts
      const allResults: ScheduleItemDto[] = [];

      for (const range of missingRanges) {
        const items = await calendarService.getCalendarItems(
          range.startDate,
          range.endDate,
        );
        allResults.push(...items);
      }

      // Attach metadata for reducer
      thunkAPI.extra = { missingRanges }; // Optional, can be handled differently

      return allResults;
    }

    // If timeMin or timeMax are missing, fetch as usual
    return await calendarService.getCalendarItems(timeMin, timeMax);
  } catch (error: any) {
    const message =
      error.response?.data?.message || error.message || error.toString();
    console.error(message);
    return thunkAPI.rejectWithValue(message);
  }
});
